import React from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { languages } from "@/lib/languages"
import CountryCodeSelector from "./CountryCodeSelector"
import { useTranslation } from "react-i18next"

const DEFAULT_DIAL_CODE = "+48"

// Everything a driver needs to exist: who they are, the number their account
// is provisioned from, and the language we write to them in. The phone is
// kept split because that is how the country selector works; join it with
// `fullPhoneNumber` right before sending.
export type DriverFieldValues = {
  firstName: string
  lastName: string
  dialCode: string
  phoneWithoutCode: string
  preferredLanguage: string
}

export const emptyDriverFields = (): DriverFieldValues => ({
  firstName: "",
  lastName: "",
  dialCode: DEFAULT_DIAL_CODE,
  phoneWithoutCode: "",
  preferredLanguage: "pl",
})

export const fullPhoneNumber = (values: DriverFieldValues) =>
  `${values.dialCode}${values.phoneWithoutCode}`

interface DriverFieldsProps {
  values: DriverFieldValues
  onChange: (values: DriverFieldValues) => void
  disabled?: boolean
  /**
   * The form creates a driver rather than editing one, so the fields the
   * account is built from - a name and the phone it is provisioned from -
   * have to be filled.
   */
  required?: boolean
  autoFocus?: boolean
}

export const DriverFields: React.FC<DriverFieldsProps> = ({
  values,
  onChange,
  disabled = false,
  required = false,
  autoFocus = false,
}) => {
  const { t } = useTranslation()
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="driverFirstName">{t("fleet.form.firstName")}</Label>
        <Input
          id="driverFirstName"
          autoFocus={autoFocus}
          required={required}
          disabled={disabled}
          value={values.firstName}
          onChange={(e) => onChange({ ...values, firstName: e.target.value })}
          className="w-full"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="driverLastName">{t("fleet.form.lastName")}</Label>
        <Input
          id="driverLastName"
          disabled={disabled}
          value={values.lastName}
          onChange={(e) => onChange({ ...values, lastName: e.target.value })}
          className="w-full"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="driverPhone">{t("fleet.form.phoneNumber")}</Label>
        <div className="flex gap-2">
          <CountryCodeSelector
            dialCode={values.dialCode}
            onChange={(country) =>
              onChange({ ...values, dialCode: country.dialCode })
            }
            disabled={disabled}
          />
          <Input
            id="driverPhone"
            required={required}
            disabled={disabled}
            value={values.phoneWithoutCode}
            onChange={(e) =>
              onChange({
                ...values,
                // The country selector owns the prefix, so the field never keeps one.
                phoneWithoutCode: e.target.value.replace(/[\s+]/g, ""),
              })
            }
            className="flex-1"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t("fleet.form.preferredLanguage")}</Label>
        <Select
          disabled={disabled}
          value={values.preferredLanguage}
          onValueChange={(preferredLanguage) =>
            onChange({ ...values, preferredLanguage })
          }
        >
          <SelectTrigger
            aria-label={t("fleet.form.preferredLanguage")}
            className="w-full"
          >
            <SelectValue placeholder={t("fleet.form.selectLanguage")} />
          </SelectTrigger>
          <SelectContent>
            {languages.map((language) => (
              <SelectItem key={language.code} value={language.code}>
                <span className="flex items-center gap-2">
                  <span>{language.flag}</span>
                  <span>{language.name}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  )
}

export default DriverFields
